import { Button, Modal } from 'antd'
import {
  StagePill,
  VersionPill,
} from 'features/libra/components/LifecyclePills'
import { getWorkflowPercComplete } from 'features/libra/helpers/getWorkflowPercComplete'
import {
  getLifecycleStage,
  isTerminalStage,
} from 'features/libra/lifecycle/lifecycleStageHelpers'
import type { LifecycleConfig } from 'features/libra/lifecycle/lifecycleTypes'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { getVersionHistory } from 'features/libra/ticket/ticketVersioning'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import type { FC } from 'react'
import { useMemo } from 'react'
import styled from 'styled-components'

const formatLocale = new Intl.DateTimeFormat().resolvedOptions().locale

function formatDate(dateStr: string | undefined): string | undefined {
  if (!dateStr) return undefined
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return undefined
  return d.toLocaleDateString(formatLocale)
}

export const TicketDetailModal: FC<{
  ticket: LibraTicket
  allTickets: ReadonlyArray<LibraTicket>
  workflow: LibraWorkflow | undefined
  lifecycleConfig: LifecycleConfig
  onClose: () => void
  onTicketClick: (ticket: LibraTicket) => void
  onCreateVersion: (ticket: LibraTicket) => void
  canCreateVersion: boolean
}> = ({
  ticket,
  allTickets,
  workflow,
  lifecycleConfig,
  onClose,
  onTicketClick,
  onCreateVersion,
  canCreateVersion,
}) => {
  const stage = getLifecycleStage(ticket, lifecycleConfig)
  const pct = Math.round(getWorkflowPercComplete(ticket, workflow))
  const terminal = isTerminalStage(ticket, lifecycleConfig)

  const versionHistory = useMemo(
    () => getVersionHistory(allTickets, ticket.assessmentId || ticket.id),
    [allTickets, ticket.assessmentId, ticket.id]
  )
  const olderVersions = useMemo(
    () => versionHistory.filter((v) => v.id !== ticket.id),
    [versionHistory, ticket.id]
  )

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={null}
      width={520}
      destroyOnClose>
      <ModalBody>
        <ModalTitle>{ticket.title}</ModalTitle>
        {workflow && <ModalWorkflow>{workflow.name}</ModalWorkflow>}

        <ModalMetaRow>
          <VersionPill>v{ticket.version}</VersionPill>
          {stage && <StagePill $color={stage.color}>{stage.name}</StagePill>}
          <PercentText>{pct}% complete</PercentText>
        </ModalMetaRow>

        <ModalActions>
          <Button type='primary' onClick={() => onTicketClick(ticket)}>
            Open Ticket
          </Button>
          {terminal && canCreateVersion && (
            <Button onClick={() => onCreateVersion(ticket)}>
              + New Version
            </Button>
          )}
        </ModalActions>

        {olderVersions.length > 0 && (
          <HistorySection>
            <HistoryTitle>
              Version History ({olderVersions.length})
            </HistoryTitle>
            {olderVersions.map((v) => {
              const vStage = getLifecycleStage(v, lifecycleConfig)
              const vPct = Math.round(getWorkflowPercComplete(v, workflow))
              const vDate = formatDate(v.updatedAt)
              return (
                <HistoryItem key={v.id}>
                  <HistoryMeta>
                    <VersionPill>v{v.version}</VersionPill>
                    {vStage ? (
                      <StagePill $color={vStage.color}>{vStage.name}</StagePill>
                    ) : (
                      <PercentText>
                        {v.workflowComplete ? 'Complete' : `${vPct}%`}
                      </PercentText>
                    )}
                    {vDate && <HistoryDate>{vDate}</HistoryDate>}
                  </HistoryMeta>
                  <Button size='small' onClick={() => onTicketClick(v)}>
                    Open
                  </Button>
                </HistoryItem>
              )
            })}
          </HistorySection>
        )}
      </ModalBody>
    </Modal>
  )
}

const PercentText = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 0.5rem;
`

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

const ModalWorkflow = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ModalMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
`

const ModalActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`

const HistorySection = styled.div`
  margin-top: 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.gray[700]};
  padding-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const HistoryTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const HistoryItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colors.gray[800]};
  border: 1px solid ${({ theme }) => theme.colors.gray[700]};
  border-radius: 6px;
  padding: 0.5rem 0.65rem;
`

const HistoryMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const HistoryDate = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`
